import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import { Box, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { trackClick } from "@/components/_helpers";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";
import { CHALLENGE_PARAM, dailyChallengeFor, formatCountdown, nextChallengeAt, utcDateKey } from "@/utils/arcade/dailyChallenge";
import { ARCADE_GAMES } from "@/utils/arcade/games";
import { arcadePath, type ArcadeSong } from "@/utils/arcade/lineup";
import { arcadeThemeFor } from "@/utils/arcade/theme";
import GameThumbnail from "../GameThumbnail";
import { getArcadeBackend, useArcadeQuery } from "./arcadeBackend";

export function challengePath(song: ArcadeSong, game: string, date: string) {
  return `${arcadePath(song.slug, game)}?${CHALLENGE_PARAM}=${date}`;
}

// Today's UTC date, rolling over at midnight without a render in between.
export function useUtcDate(): string {
  const [date, setDate] = useState(() => utcDateKey(Date.now()));
  useEffect(() => {
    const timer = window.setTimeout(() => setDate(utcDateKey(Date.now())), nextChallengeAt(Date.now()) - Date.now() + 500);
    return () => window.clearTimeout(timer);
  }, [date]);
  return date;
}

// Only this line ticks every second.
export function ChallengeCountdown({ prefix = "New challenge in" }: { prefix?: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  return (
    <>
      {prefix} {formatCountdown(nextChallengeAt(now) - now)}
    </>
  );
}

const eyebrowSx = { fontFamily: "Poppins, sans-serif", fontSize: "12px", fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase" } as const;

// Today's song and game for everyone, with a countdown to the next one and
// the top three so far. The board is read only once the card is on screen.
export function DailyChallengeCard({ lineup }: { lineup: ArcadeSong[] }) {
  const date = useUtcDate();
  const [ref, seen] = useIntersectionObserver<HTMLAnchorElement>({ rootMargin: "200px" });
  const pick = useMemo(() => dailyChallengeFor(lineup, date, (song, game) => arcadeThemeFor(song).games.includes(game)), [lineup, date]);
  const theme = useMemo(() => (pick ? arcadeThemeFor(pick.song) : null), [pick]);
  // Without Convex (local dev, previews) there is no board to read.
  const backendReady = getArcadeBackend().ready;
  const board = useArcadeQuery("board", pick && seen && backendReady ? { songSlug: pick.song.slug, game: pick.game, period: "today", challenge: date } : "skip");

  if (!pick || !theme) return null;
  const info = ARCADE_GAMES[pick.game];
  const to = challengePath(pick.song, pick.game, date);
  const top = board.status === "ready" ? board.value.entries.slice(0, 3) : [];
  const palette = theme.palette;

  return (
    <Box
      ref={ref}
      component={RouterLink}
      to={to}
      onClick={() => trackClick("arcade:challenge-card", { label: `Daily challenge card ${date}`, section: "arcade", track: pick.song.title, href: to })}
      aria-label={`Daily challenge: ${info.name} with ${pick.song.title}`}
      sx={{
        position: "relative",
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.1fr) minmax(0, 1fr)" },
        gap: { xs: "16px", md: "28px" },
        alignItems: "center",
        mt: { xs: "2rem", md: "2.5rem" },
        p: { xs: "18px", md: "28px 32px" },
        borderRadius: "26px",
        overflow: "hidden",
        textDecoration: "none",
        color: "#fff",
        background: `radial-gradient(80% 140% at 0% 0%, color-mix(in srgb, ${palette.primary} 40%, transparent), transparent 60%), linear-gradient(135deg, #0b0b14, #120f1c)`,
        border: `1px solid color-mix(in srgb, ${palette.primary} 45%, transparent)`,
        boxShadow: `0 30px 70px -40px ${palette.primary}`,
        transition: "transform 0.3s ease, box-shadow 0.3s ease",
        "&:hover": { transform: "translateY(-3px)", color: "#fff", boxShadow: `0 34px 80px -36px ${palette.primary}` },
        "&:focus-visible": { outline: `2px solid ${palette.accent}`, outlineOffset: "3px" },
      }}
    >
      <Box sx={{ position: "relative", aspectRatio: "16 / 9", borderRadius: "18px", overflow: "hidden", order: { xs: 0, md: 1 } }}>
        <GameThumbnail kind={pick.game} theme={theme} coverUrl={pick.song.artwork} width={480} height={270} alt={`${info.name} with ${pick.song.title}`} />
        <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(0,0,0,0) 45%, rgba(0,0,0,0.75))" }} />
        <Box sx={{ position: "absolute", left: "12px", bottom: "10px", display: "inline-flex", alignItems: "center", gap: "6px", px: "10px", py: "5px", borderRadius: "999px", backgroundColor: "rgba(0,0,0,0.6)", backdropFilter: "blur(6px)" }}>
          <TimerOutlinedIcon sx={{ fontSize: 16, color: palette.accent }} />
          <Typography component="span" sx={{ fontFamily: "Poppins, sans-serif", fontSize: "13px", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
            <ChallengeCountdown />
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "grid", gap: "10px", minWidth: 0 }}>
        <Typography sx={{ ...eyebrowSx, color: palette.accent }}>Daily challenge</Typography>
        <Typography component="h2" sx={{ m: 0, fontFamily: "Hanson", fontWeight: 700, fontSize: { xs: "2.6rem", md: "2.1rem" }, lineHeight: 1.05, textShadow: `0 0 26px color-mix(in srgb, ${palette.primary} 60%, transparent)` }}>
          {info.name}
        </Typography>
        <Typography sx={{ fontFamily: "Poppins, sans-serif", fontSize: { xs: "15px", md: "15px" }, color: "rgba(255,255,255,0.82)", lineHeight: 1.5 }}>
          with {pick.song.title}. One song, one game, one board for everyone today. Post your best before it resets.
        </Typography>
        <Box component="ol" aria-label="Today's top three" sx={{ listStyle: "none", m: 0, p: "8px 10px", borderRadius: "14px", backgroundColor: "rgba(255,255,255,0.06)", display: "grid", gap: "2px", minHeight: "86px" }}>
          {top.length === 0 ? (
            <Typography component="li" sx={{ fontFamily: "Poppins, sans-serif", fontSize: "14px", color: "rgba(255,255,255,0.75)", py: "6px", display: "inline-flex", alignItems: "center", gap: "8px" }}>
              <EmojiEventsIcon sx={{ fontSize: 18, color: palette.accent }} />
              {board.status === "loading" && seen && backendReady ? "Loading today's board..." : "No scores yet today. The top spot is open."}
            </Typography>
          ) : (
            top.map((entry) => (
              <Box component="li" key={`${entry.rank}-${entry.nickname}`} sx={{ display: "grid", gridTemplateColumns: "24px minmax(0, 1fr) auto", gap: "8px", alignItems: "center", minHeight: "26px" }}>
                <Typography component="span" sx={{ fontFamily: "Poppins, sans-serif", fontSize: "13px", fontWeight: 800, color: palette.accent }}>{entry.rank}</Typography>
                <Typography component="span" sx={{ fontFamily: "Poppins, sans-serif", fontSize: "14px", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{entry.nickname}</Typography>
                <Typography component="span" sx={{ fontFamily: "Poppins, sans-serif", fontSize: "14px", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{entry.score.toLocaleString()}</Typography>
              </Box>
            ))
          )}
        </Box>
        <Box sx={{ display: "inline-flex", alignItems: "center", justifySelf: "start", gap: "8px", minHeight: "48px", px: "22px", borderRadius: "999px", fontFamily: "Poppins, sans-serif", fontWeight: 700, fontSize: "15px", color: palette.backgroundDeep, background: `linear-gradient(135deg, ${palette.accent}, ${palette.primary})` }}>
          <PlayArrowIcon sx={{ fontSize: 20 }} /> Play today's challenge
        </Box>
      </Box>
    </Box>
  );
}

export default DailyChallengeCard;
